'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDynamicContext, useIsLoggedIn } from '@dynamic-labs/sdk-react-core';
import { isUserRejectionError } from '@/lib/wallet-errors';

export type WalletSignerHealthStatus =
  | 'idle'
  | 'checking'
  | 'active'
  | 'disconnected'
  | 'mismatch'
  | 'unavailable';

export type WalletSignerRecoveryOutcome = 'succeeded' | 'rejected' | 'failed';

interface WalletSignerHealth {
  status: WalletSignerHealthStatus;
  isSignerReady: boolean;
  /** Re-checks the browser wallet without opening a wallet prompt. */
  retry: () => Promise<WalletSignerRecoveryOutcome>;
  /**
   * Direct user action that asks the connector to restore account permission,
   * then verifies the live account without invoking Dynamic's sync modal.
   */
  reconnect: () => Promise<WalletSignerRecoveryOutcome>;
}

interface WalletSignerHealthProviderProps {
  children: React.ReactNode;
  /** Primarily exposed so tests can verify the bounded failure state quickly. */
  probeTimeoutMs?: number;
  /** Maximum time an interactive connector permission request may remain open. */
  reconnectTimeoutMs?: number;
}

type SignerConnector = NonNullable<
  ReturnType<typeof useDynamicContext>['primaryWallet']
>['connector'];

interface SignerHealthSnapshot {
  connector: SignerConnector | null;
  expectedAddress: string | undefined;
  status: WalletSignerHealthStatus;
}

const DEFAULT_SIGNER_HEALTH_PROBE_TIMEOUT_MS = 2_000;
const DEFAULT_SIGNER_RECONNECT_TIMEOUT_MS = 60_000;

const WalletSignerHealthContext = createContext<WalletSignerHealth | null>(
  null
);

function addressesMatch(first: string | undefined, second: string | undefined) {
  return (
    first != null &&
    second != null &&
    first.toLowerCase() === second.toLowerCase()
  );
}

function getHealthStatus(
  accounts: unknown,
  expectedAddress: string
): WalletSignerHealthStatus {
  if (!Array.isArray(accounts)) return 'unavailable';

  const activeAddress = accounts[0];
  if (typeof activeAddress !== 'string' || activeAddress.length === 0) {
    return 'disconnected';
  }

  return addressesMatch(activeAddress, expectedAddress) ? 'active' : 'mismatch';
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error('timeout')),
      timeoutMs
    );

    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/**
 * Tracks whether Dynamic's authenticated primary wallet is also the browser
 * wallet's active signer. Dynamic's wagmi adapter can expose the persisted
 * primary address even when the extension has no active account, so this
 * provider checks the connector's live, non-prompting account list instead.
 *
 * Mount this once beneath DynamicContextProvider. Consumers share this one
 * probe and one pair of connector listeners; reading the context never starts
 * another provider request. The provider never polls and never exposes the
 * active or expected account value.
 */
export function WalletSignerHealthProvider({
  children,
  probeTimeoutMs = DEFAULT_SIGNER_HEALTH_PROBE_TIMEOUT_MS,
  reconnectTimeoutMs = DEFAULT_SIGNER_RECONNECT_TIMEOUT_MS
}: WalletSignerHealthProviderProps) {
  const { sdkHasLoaded, primaryWallet } = useDynamicContext();
  const isLoggedIn = useIsLoggedIn();
  const requestIdRef = useRef(0);
  const reconnectInFlightRef = useRef<{
    connector: SignerConnector;
    expectedAddress: string;
    promise: Promise<WalletSignerRecoveryOutcome>;
  } | null>(null);
  const connector = primaryWallet?.connector;
  const expectedAddress = primaryWallet?.address;
  const canProbe = Boolean(
    sdkHasLoaded && isLoggedIn && connector && expectedAddress
  );
  const currentTargetRef = useRef({ canProbe, connector, expectedAddress });
  currentTargetRef.current = { canProbe, connector, expectedAddress };
  const [snapshot, setSnapshot] = useState<SignerHealthSnapshot>({
    connector: null,
    expectedAddress: undefined,
    status: 'idle'
  });

  const probe = useCallback(async (): Promise<WalletSignerHealthStatus> => {
    const requestId = ++requestIdRef.current;

    if (!canProbe || !connector || !expectedAddress) {
      setSnapshot({
        connector: null,
        expectedAddress: undefined,
        status: 'idle'
      });
      return 'idle';
    }

    const probeTarget = { connector, expectedAddress };
    setSnapshot({ ...probeTarget, status: 'checking' });

    try {
      const accounts = await withTimeout(
        Promise.resolve(connector.getConnectedAccounts()),
        probeTimeoutMs
      );

      if (requestId !== requestIdRef.current) return 'unavailable';

      const nextStatus = getHealthStatus(accounts, expectedAddress);
      setSnapshot({ ...probeTarget, status: nextStatus });
      return nextStatus;
    } catch {
      if (requestId !== requestIdRef.current) return 'unavailable';

      // Provider and SDK errors can contain wallet addresses. Deliberately
      // collapse every failure to a coarse state without logging the error.
      setSnapshot({ ...probeTarget, status: 'unavailable' });
      return 'unavailable';
    }
  }, [canProbe, connector, expectedAddress, probeTimeoutMs]);

  const reconnect = useCallback((): Promise<WalletSignerRecoveryOutcome> => {
    if (!canProbe || !connector || !expectedAddress) {
      return Promise.resolve('failed');
    }

    const existingAttempt = reconnectInFlightRef.current;
    if (
      existingAttempt?.connector === connector &&
      addressesMatch(existingAttempt.expectedAddress, expectedAddress)
    ) {
      return existingAttempt.promise;
    }

    const reconnectRequestId = ++requestIdRef.current;
    const reconnectTarget = { connector, expectedAddress };
    setSnapshot({ ...reconnectTarget, status: 'checking' });

    const promise: Promise<WalletSignerRecoveryOutcome> = (async () => {
      try {
        await withTimeout(
          Promise.resolve(connector.getAddress()),
          reconnectTimeoutMs
        );

        const currentTarget = currentTargetRef.current;
        const targetIsStillCurrent =
          currentTarget.canProbe &&
          currentTarget.connector === connector &&
          addressesMatch(currentTarget.expectedAddress, expectedAddress);

        if (!targetIsStillCurrent) return 'failed';

        // Always perform a final authoritative read. Some connectors emit an
        // accountChange before their permission request has fully settled;
        // relying on that intermediate event can leave a stale mismatch.
        const nextStatus = await probe();
        return nextStatus === 'active' ? 'succeeded' : 'failed';
      } catch (error: unknown) {
        const currentTarget = currentTargetRef.current;
        const targetIsStillCurrent =
          currentTarget.canProbe &&
          currentTarget.connector === connector &&
          addressesMatch(currentTarget.expectedAddress, expectedAddress);

        if (targetIsStillCurrent && isUserRejectionError(error)) {
          // Restore the non-prompting live state after a normal user cancel.
          // The raw provider error is deliberately neither exposed nor logged.
          await probe();
          return 'rejected';
        }

        if (
          targetIsStillCurrent &&
          requestIdRef.current === reconnectRequestId
        ) {
          setSnapshot({ ...reconnectTarget, status: 'unavailable' });
        }
        return 'failed';
      }
    })();

    reconnectInFlightRef.current = {
      ...reconnectTarget,
      promise
    };

    const clearAttempt = () => {
      if (reconnectInFlightRef.current?.promise === promise) {
        reconnectInFlightRef.current = null;
      }
    };
    void promise.then(clearAttempt, clearAttempt);

    return promise;
  }, [canProbe, connector, expectedAddress, probe, reconnectTimeoutMs]);

  useEffect(() => {
    if (!canProbe || !connector) {
      requestIdRef.current += 1;
      setSnapshot({
        connector: null,
        expectedAddress: undefined,
        status: 'idle'
      });
      return;
    }

    const recheck = () => {
      void probe();
    };

    connector.on('accountChange', recheck);
    connector.on('disconnect', recheck);
    void probe();

    return () => {
      // Invalidate a response from the previous wallet before removing its
      // listeners, so it cannot overwrite the next wallet's health state.
      requestIdRef.current += 1;
      connector.off('accountChange', recheck);
      connector.off('disconnect', recheck);
    };
  }, [canProbe, connector, probe]);

  const status = useMemo<WalletSignerHealthStatus>(() => {
    if (!canProbe || !connector || !expectedAddress) return 'idle';

    // Fail closed during the render in which the primary wallet changes. The
    // effect will begin its probe after commit, but consumers must not observe
    // the previous wallet's `active` result for even one render.
    const snapshotBelongsToCurrentWallet =
      snapshot.connector === connector &&
      addressesMatch(snapshot.expectedAddress, expectedAddress);

    return snapshotBelongsToCurrentWallet ? snapshot.status : 'checking';
  }, [canProbe, connector, expectedAddress, snapshot]);

  const value = useMemo<WalletSignerHealth>(
    () => ({
      status,
      isSignerReady: status === 'active',
      retry: async () =>
        (await probe()) === 'active' ? 'succeeded' : 'failed',
      reconnect
    }),
    [probe, reconnect, status]
  );

  return (
    <WalletSignerHealthContext.Provider value={value}>
      {children}
    </WalletSignerHealthContext.Provider>
  );
}

export function useWalletSignerHealth(): WalletSignerHealth {
  const context = useContext(WalletSignerHealthContext);

  if (!context) {
    throw new Error(
      'useWalletSignerHealth must be used within WalletSignerHealthProvider'
    );
  }

  return context;
}
